import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Palette, Type, Layout, Eye, Save, RotateCcw, Monitor, Moon, Sun, Check, ImageIcon, Loader2, Link, Mail, Smartphone, Tablet, EyeOff, Search, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { graphqlRequest } from "@/utils/graphql";
import { validateImageFile, hostMediaGoService } from "@/utils/media-upload";
import { getAllBrands, type Brand } from "@/utils/brands-api";
import { toast } from "sonner";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type EmailFrequency = "daily" | "per_transaction" | "off";

type SDKSettings = {
  primary_color: string;
  accent_color: string;
  background_color: string;
  surface_color: string;
  text_color: string;
  text_secondary_color: string;
  text_muted_color: string;
  on_accent_color: string;
  font_family: string;
  border_radius: "sharp" | "rounded" | "pill";
  mode: "light" | "dark";
  logo_url: string;
  store_title: string;
  cta_text: string;
  hide_sections: string[];
  hidden_service_ids: string[];
  email_frequency: EmailFrequency;
};

const DEFAULTS: SDKSettings = {
  primary_color: "#1E293B",
  accent_color: "#059669",
  background_color: "#FFFFFF",
  surface_color: "#FFFFFF",
  text_color: "#111827",
  text_secondary_color: "#6B7280",
  text_muted_color: "",
  on_accent_color: "",
  font_family: "",
  border_radius: "rounded",
  mode: "light",
  logo_url: "",
  store_title: "Rewards Store",
  cta_text: "",
  hide_sections: [],
  hidden_service_ids: [],
  email_frequency: "daily",
};

/* ------------------------------------------------------------------ */
/*  Preset Themes (same as sdk-test.html)                              */
/* ------------------------------------------------------------------ */

const PRESET_THEMES = [
  { name: "Default",  primary: "#1E293B", accent: "#059669", dot: "#059669" },
  { name: "Orange",   primary: "#FF6B00", accent: "#E85D04", dot: "#E85D04" },
  { name: "Purple",   primary: "#7C3AED", accent: "#6D28D9", dot: "#7C3AED" },
  { name: "Red",      primary: "#DC2626", accent: "#B91C1C", dot: "#DC2626" },
  { name: "Teal",     primary: "#0891B2", accent: "#0E7490", dot: "#0891B2" },
  { name: "Pink",     primary: "#DB2777", accent: "#BE185D", dot: "#DB2777" },
];

/* ------------------------------------------------------------------ */
/*  Queries                                                            */
/* ------------------------------------------------------------------ */

const FETCH_QUERY = `
  query GetSDKSettings($user_id: uuid!) {
    whatsub_b2b_client(where: { user_id: { _eq: $user_id } }) {
      id
      whatsub_b2b_client_sdk_setting: whatsub_b2b_client_sdk_setting {
        primary_color
        accent_color
        background_color
        surface_color
        text_color
        text_secondary_color
        text_muted_color
        on_accent_color
        font_family
        border_radius
        mode
        logo_url
        store_title
        cta_text
        hide_sections
        hidden_service_ids
        email_frequency
      }
    }
  }
`;

const UPSERT_MUTATION = `
  mutation UpsertSDKSettings(
    $client_id: uuid!,
    $primary_color: String,
    $accent_color: String,
    $background_color: String,
    $surface_color: String,
    $text_color: String,
    $text_secondary_color: String,
    $text_muted_color: String,
    $on_accent_color: String,
    $font_family: String,
    $border_radius: String,
    $mode: String,
    $logo_url: String,
    $store_title: String,
    $cta_text: String,
    $hide_sections: jsonb,
    $hidden_service_ids: [uuid!],
    $email_frequency: String
  ) {
    insert_whatsub_b2b_client_sdk_settings_one(
      object: {
        client_id: $client_id,
        primary_color: $primary_color,
        accent_color: $accent_color,
        background_color: $background_color,
        surface_color: $surface_color,
        text_color: $text_color,
        text_secondary_color: $text_secondary_color,
        text_muted_color: $text_muted_color,
        on_accent_color: $on_accent_color,
        font_family: $font_family,
        border_radius: $border_radius,
        mode: $mode,
        logo_url: $logo_url,
        store_title: $store_title,
        cta_text: $cta_text,
        hide_sections: $hide_sections,
        hidden_service_ids: $hidden_service_ids,
        email_frequency: $email_frequency
      },
      on_conflict: {
        constraint: whatsub_b2b_client_sdk_settings_client_id_key,
        update_columns: [
          primary_color, accent_color, background_color, surface_color,
          text_color, text_secondary_color, text_muted_color, on_accent_color,
          font_family, border_radius, mode, logo_url, store_title, cta_text, hide_sections,
          hidden_service_ids, email_frequency
        ]
      }
    ) { id }
  }
`;

/* ------------------------------------------------------------------ */
/*  Small components                                                   */
/* ------------------------------------------------------------------ */

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 rounded-md border border-border cursor-pointer p-0" />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="#000000" className="w-24 text-xs font-mono px-2 py-1.5 rounded-md border border-border bg-background" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Live Preview                                                       */
/* ------------------------------------------------------------------ */

type DeviceView = "mobile" | "tablet" | "desktop";

// Natural viewport per device — the iframe renders at these true pixel
// dimensions so the SDK's responsive layout reflects real device behaviour.
// The visible frame is transform:scale'd to fit the right column, which itself
// widens per device (see PARENT_COLUMN_WIDTH) so desktop/tablet stay legible.
const DEVICE_FRAMES: Record<DeviceView, {
  width: number;
  height: number;
  frameRadius: number;
  borderWidth: number;
  hasNotch: boolean;
  label: string;
  Icon: typeof Smartphone;
}> = {
  mobile:  { width: 320,  height: 640, frameRadius: 36, borderWidth: 6, hasNotch: true,  label: "Mobile",  Icon: Smartphone },
  tablet:  { width: 620,  height: 820, frameRadius: 26, borderWidth: 8, hasNotch: false, label: "Tablet",  Icon: Tablet     },
  desktop: { width: 1024, height: 640, frameRadius: 12, borderWidth: 8, hasNotch: false, label: "Desktop", Icon: Monitor    },
};

// Right-column footprint per device. The column itself resizes so a desktop
// preview gets ~720px to render into (scale ≈ 0.68) instead of being squished
// into the mobile 340px slot (scale ≈ 0.33).
export const PARENT_COLUMN_WIDTH: Record<DeviceView, number> = {
  mobile:  360,
  tablet:  560,
  desktop: 720,
};

const columnGutter = 20; // preview stage width = column - gutter

function SDKPreview({
  settings,
  clientId,
  authToken,
  device,
  onDeviceChange,
}: {
  settings: SDKSettings;
  clientId: string;
  authToken?: string;
  device: DeviceView;
  onDeviceChange: (d: DeviceView) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const dims = DEVICE_FRAMES[device];
  const maxPreviewWidth = PARENT_COLUMN_WIDTH[device] - columnGutter;
  const scale = Math.min(1, maxPreviewWidth / dims.width);

  // Send theme to iframe via postMessage whenever settings change
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const sendTheme = () => {
      // Only send non-default colors so dark/light mode logic works properly
      const D = DEFAULTS;
      const payload: Record<string, unknown> = {
        primaryColor: settings.primary_color || "#1E293B",
        accentColor: settings.accent_color || "#059669",
        borderRadius: settings.border_radius || "rounded",
        mode: settings.mode || "light",
      };
      if (settings.background_color && settings.background_color !== D.background_color) payload.backgroundColor = settings.background_color;
      if (settings.surface_color && settings.surface_color !== D.surface_color) payload.surfaceColor = settings.surface_color;
      if (settings.text_color && settings.text_color !== D.text_color) payload.textColor = settings.text_color;
      if (settings.text_secondary_color && settings.text_secondary_color !== D.text_secondary_color) payload.textSecondaryColor = settings.text_secondary_color;
      if (settings.text_muted_color) payload.textMutedColor = settings.text_muted_color;
      if (settings.on_accent_color) payload.onAccentColor = settings.on_accent_color;
      if (settings.font_family) payload.fontFamily = settings.font_family;
      if (settings.logo_url) payload.logoUrl = settings.logo_url;
      if (settings.store_title) payload.storeTitle = settings.store_title;
      if (settings.cta_text) payload.ctaText = settings.cta_text;

      iframe.contentWindow?.postMessage({ type: "SET_THEME", payload }, "*");
    };

    // Send immediately + small delay for iframe load
    sendTheme();
    const timer = setTimeout(sendTheme, 500);
    return () => clearTimeout(timer);
  }, [settings]);

  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const handleLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    // Reuse the same sendTheme via a small delay
    setTimeout(() => {
      const s = settingsRef.current;
      const D = DEFAULTS;
      const payload: Record<string, unknown> = {
        primaryColor: s.primary_color || "#1E293B",
        accentColor: s.accent_color || "#059669",
        borderRadius: s.border_radius || "rounded",
        mode: s.mode || "light",
      };
      if (s.background_color && s.background_color !== D.background_color) payload.backgroundColor = s.background_color;
      if (s.surface_color && s.surface_color !== D.surface_color) payload.surfaceColor = s.surface_color;
      if (s.text_color && s.text_color !== D.text_color) payload.textColor = s.text_color;
      if (s.text_secondary_color && s.text_secondary_color !== D.text_secondary_color) payload.textSecondaryColor = s.text_secondary_color;
      if (s.text_muted_color) payload.textMutedColor = s.text_muted_color;
      if (s.on_accent_color) payload.onAccentColor = s.on_accent_color;
      if (s.font_family) payload.fontFamily = s.font_family;
      if (s.logo_url) payload.logoUrl = s.logo_url;
      if (s.store_title) payload.storeTitle = s.store_title;
      if (s.cta_text) payload.ctaText = s.cta_text;
      iframe.contentWindow?.postMessage({ type: "SET_THEME", payload }, "*");
    }, 500);
  };

  const SDK_BASE = "https://sdk.pocoai.store";
  const sdkUrl = authToken
    ? `${SDK_BASE}/store?auth=${encodeURIComponent(authToken)}&client=${clientId}`
    : `${SDK_BASE}/store?client=${clientId}`;

  return (
    <div className="w-full flex flex-col items-center">
      {/* Device switcher */}
      <div className="flex items-center gap-1 mb-4 p-1 rounded-lg border border-border bg-muted/40">
        {(Object.keys(DEVICE_FRAMES) as DeviceView[]).map((key) => {
          const { Icon, label } = DEVICE_FRAMES[key];
          const active = device === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onDeviceChange(key)}
              title={`Preview at ${label} viewport (${DEVICE_FRAMES[key].width}×${DEVICE_FRAMES[key].height})`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                active
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Scaled preview stage — the outer wrapper reserves the visual footprint
          (scaled height), the inner div contains the natural-size frame that
          transform:scale shrinks. Iframe still renders at true viewport pixels
          so the SDK's responsive layout reflects real device behaviour. */}
      <div
        className="relative"
        style={{
          width: dims.width * scale,
          height: dims.height * scale,
        }}
      >
        <div
          className="absolute top-0 left-0 origin-top-left"
          style={{
            width: dims.width,
            height: dims.height,
            transform: `scale(${scale})`,
          }}
        >
          <div
            className="relative w-full h-full"
            style={{
              borderRadius: dims.frameRadius,
              border: `${dims.borderWidth}px solid #1a1a2e`,
              boxShadow: "0 8px 32px rgba(0,0,0,0.2), inset 0 0 0 2px #2d2d4e",
              overflow: "hidden",
              backgroundColor: "#000",
            }}
          >
            {/* Dynamic Island — mobile only */}
            {dims.hasNotch && (
              <div
                className="absolute top-[6px] left-1/2 -translate-x-1/2 z-10"
                style={{
                  width: 80,
                  height: 18,
                  backgroundColor: "#000",
                  borderRadius: 12,
                }}
              />
            )}

            {/* Iframe */}
            <iframe
              ref={iframeRef}
              src={sdkUrl}
              onLoad={handleLoad}
              className="w-full border-0"
              title="SDK Preview"
              style={
                dims.hasNotch
                  ? { borderRadius: dims.frameRadius - 6, height: "calc(100% - 28px)", marginTop: 28 }
                  : { borderRadius: dims.frameRadius - 6, height: "100%" }
              }
            />
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center mt-3">
        Live preview — {dims.width}×{dims.height} · changes reflect in real-time
      </p>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/*  Logo Uploader                                                      */
/* ------------------------------------------------------------------ */

function LogoUploader({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const { token } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [showUrlInput, setShowUrlInput] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !token) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error ?? "Invalid file");
      return;
    }

    setError("");
    setUploading(true);

    const result = await hostMediaGoService(file, "sdk-logos", token);

    if (result.success && result.url) {
      onChange(result.url);
      toast("Logo uploaded successfully");
    } else {
      setError(result.error ?? "Upload failed");
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block">Logo</label>

      {value ? (
        /* ── Preview ── */
        <div className="relative group rounded-lg border border-border bg-muted/30 p-4 flex items-center gap-4">
          <div className="w-[200px] h-[60px] rounded-md border border-border bg-background flex items-center justify-center overflow-hidden shrink-0">
            <img src={value} alt="Logo" className="max-w-full max-h-full object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground truncate">{value}</p>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-xs font-medium text-foreground hover:underline underline-offset-2"
              >
                Replace
              </button>
              <button
                type="button"
                onClick={() => onChange("")}
                className="text-xs font-medium text-red-500 hover:underline underline-offset-2"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ── Upload area ── */
        <div
          className="rounded-lg border-2 border-dashed border-border hover:border-foreground/30 transition-colors cursor-pointer p-6 flex flex-col items-center gap-3 text-center"
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
          <div>
            <p className="text-sm font-medium">
              {uploading ? "Uploading..." : "Click to upload logo"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Recommended: 200 x 60px &middot; PNG or SVG with transparent background
            </p>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 mt-1.5">{error}</p>
      )}

      {/* Paste URL fallback */}
      {!value && (
        <div className="mt-2">
          {showUrlInput ? (
            <div className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="https://example.com/logo.png"
                className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const val = (e.target as HTMLInputElement).value.trim();
                    if (val) { onChange(val); setShowUrlInput(false); }
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value.trim();
                  if (val) { onChange(val); setShowUrlInput(false); }
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowUrlInput(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowUrlInput(true)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Link className="h-3 w-3" />
              Or paste a URL
            </button>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-1.5">Replaces the default logo in the SDK header</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Hidden Brands picker                                               */
/* ------------------------------------------------------------------ */

function HiddenBrandsCard({
  brands,
  loading,
  hiddenIds,
  onChange,
}: {
  brands: Brand[];
  loading: boolean;
  hiddenIds: string[];
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState("");

  // Cheap membership check for the toggle row — allocated once per re-render.
  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);

  const hiddenBrands = useMemo(
    () => brands.filter((b) => hiddenSet.has(b.brandId)),
    [brands, hiddenSet],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? brands.filter((b) => b.brandName.toLowerCase().includes(q)) : brands;
    // Show hidden brands first so the user can un-hide them without scrolling.
    return [...base].sort((a, b) => {
      const ah = hiddenSet.has(a.brandId) ? 0 : 1;
      const bh = hiddenSet.has(b.brandId) ? 0 : 1;
      if (ah !== bh) return ah - bh;
      return a.brandName.localeCompare(b.brandName);
    });
  }, [brands, query, hiddenSet]);

  function toggle(brandId: string) {
    onChange(
      hiddenSet.has(brandId)
        ? hiddenIds.filter((id) => id !== brandId)
        : [...hiddenIds, brandId],
    );
  }

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <EyeOff className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-base font-semibold">Hidden Brands</h3>
        </div>
        <Separator className="mb-4" />
        <p className="text-xs text-muted-foreground mb-3">
          Hide specific brands from your users. Hidden brands won&apos;t appear in the store, search, category listings, or subscription groups, and direct links to them redirect back to the store.
        </p>

        {/* Currently hidden — quick-remove chips */}
        {hiddenBrands.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {hiddenBrands.map((b) => (
              <button
                key={b.brandId}
                onClick={() => toggle(b.brandId)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-xs font-medium hover:bg-muted/70 transition-colors"
                title="Un-hide"
              >
                {b.brandName}
                <X className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}

        {/* Search input */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search brands..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
          />
        </div>

        {/* Brand list */}
        <div className="max-h-80 overflow-y-auto rounded-lg border border-border">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-10">
              {query.trim() ? `No brands match "${query}"` : "No brands available"}
            </p>
          ) : (
            filtered.map((b) => {
              const isHidden = hiddenSet.has(b.brandId);
              return (
                <button
                  key={b.brandId}
                  onClick={() => toggle(b.brandId)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/40 transition-colors text-left border-b border-border last:border-b-0"
                >
                  <div className="w-8 h-8 rounded-md bg-muted flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {b.logoURL ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={b.logoURL} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-semibold text-muted-foreground">
                        {b.brandName[0]}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{b.brandName}</p>
                  </div>
                  <div
                    className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                      isHidden ? "bg-primary border-primary" : "border-muted-foreground/40"
                    }`}
                  >
                    {isHidden && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {!loading && (
          <p className="text-[11px] text-muted-foreground mt-2">
            {hiddenIds.length} of {brands.length} brands hidden
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function SDKConfigurationPage() {
  const { token, userId } = useAuth();
  const [settings, setSettings] = useState<SDKSettings>(DEFAULTS);
  const [clientId, setClientId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showCustomColors, setShowCustomColors] = useState(false);
  // Preview device is lifted from SDKPreview so the right-column width can
  // resize with it — otherwise tablet/desktop previews would squish into the
  // mobile-sized slot.
  const [previewDevice, setPreviewDevice] = useState<DeviceView>("mobile");

  // Brand catalogue for the Hidden Brands picker. Loaded once via the same
  // action the discount-mapping page uses, so the list matches what the SDK
  // shows this partner today. Fetched in parallel with settings — the picker
  // shows a spinner state until it arrives.
  const [brandCatalog, setBrandCatalog] = useState<Brand[]>([]);
  const [brandCatalogLoading, setBrandCatalogLoading] = useState(true);

  const update = useCallback((key: keyof SDKSettings, value: string | string[]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }, []);

  const applyPreset = useCallback((preset: typeof PRESET_THEMES[0]) => {
    setSettings((prev) => ({
      ...prev,
      primary_color: preset.primary,
      accent_color: preset.accent,
    }));
    setHasChanges(true);
  }, []);

  const activePreset = PRESET_THEMES.find(
    (p) => p.primary === settings.primary_color && p.accent === settings.accent_color
  );

  // Fetch existing settings
  useEffect(() => {
    if (!token || !userId) return;

    graphqlRequest<{
      whatsub_b2b_client: Array<{
        id: string;
        whatsub_b2b_client_sdk_setting: SDKSettings | null;
      }>;
    }>(FETCH_QUERY, { user_id: userId }, token)
      .then((data) => {
        const client = data.whatsub_b2b_client?.[0];
        if (client) {
          setClientId(client.id);
          const existing = client.whatsub_b2b_client_sdk_setting;
          if (existing) {
            setSettings({ ...DEFAULTS, ...Object.fromEntries(Object.entries(existing).filter(([, v]) => v != null)) });
          }
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [token, userId]);

  // Brand catalogue — used by the Hidden Brands picker. Runs alongside the
  // settings fetch, not gated on it, so the two land independently.
  //
  // Two sources are unioned by service_id:
  //  1) getAllBrandsByUser — every brand with a sellable gift-card plan.
  //  2) whatsub_services referenced by public subscription groups — brands
  //     that ship subscription-only (Claude, Canva, Netflix, etc.) and
  //     therefore never appear in (1). Without this the partner cannot hide
  //     any subscription-only brand from their SDK. We resolve names/logos
  //     from `whatsub_services` directly (not from group names) so the
  //     picker shows "Canva" not "Canva pro yearly plan".
  useEffect(() => {
    if (!token || !userId) return;
    Promise.all([
      getAllBrands(userId, token).catch(() => [] as Brand[]),
      graphqlRequest<{
        w_getPublicGroups: Array<{ whatsub_plans: { service_id: string } | null }>;
      }>(
        `query { w_getPublicGroups { whatsub_plans } }`,
        {},
        token,
      )
        .then((data) => data.w_getPublicGroups ?? [])
        .catch(() => []),
    ])
      .then(async ([gcBrands, groups]) => {
        const byId = new Map<string, Brand>();
        for (const b of gcBrands) byId.set(b.brandId, b);

        // Collect subscription-only service_ids the partner hasn't seen yet.
        const missing = new Set<string>();
        for (const g of groups) {
          const sid = g.whatsub_plans?.service_id;
          if (sid && !byId.has(sid)) missing.add(sid);
        }

        if (missing.size > 0) {
          try {
            const svcData = await graphqlRequest<{
              whatsub_services: Array<{ id: string; service_name: string; image_url: string | null }>;
            }>(
              `query GetServicesByIds($ids: [uuid!]) {
                whatsub_services(where: { id: { _in: $ids } }) {
                  id
                  service_name
                  image_url
                }
              }`,
              { ids: Array.from(missing) },
              token,
            );
            for (const s of svcData.whatsub_services ?? []) {
              byId.set(s.id, {
                brandId: s.id,
                brandName: s.service_name,
                about: "",
                logoURL: s.image_url ?? "",
                logoBlurhash: "",
                maxDiscount: 0,
              });
            }
          } catch {
            // Non-fatal — subscription-only brands just stay out of the picker.
          }
        }

        setBrandCatalog(Array.from(byId.values()));
      })
      .finally(() => setBrandCatalogLoading(false));
  }, [token, userId]);

  async function handleSave() {
    if (!clientId || !token) {
      toast.error("No client found for your account");
      return;
    }
    setIsSaving(true);
    try {
      await graphqlRequest(
        UPSERT_MUTATION,
        {
          client_id: clientId,
          ...settings,
          hide_sections: settings.hide_sections,
          // The DB column is `[uuid]!` (non-null array) — always send an
          // array, even when empty, or Hasura will reject the mutation.
          hidden_service_ids: settings.hidden_service_ids ?? [],
        },
        token,
      );
      toast.success("SDK settings saved successfully");
      setHasChanges(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  }

  function handleReset() {
    setSettings(DEFAULTS);
    setHasChanges(true);
  }

  if (isLoading) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold">SDK Configuration</h1>
          <p className="text-muted-foreground">Customize how the SDK looks in your app.</p>
        </div>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 rounded-full border-[3px] border-muted animate-spin" style={{ borderTopColor: "hsl(var(--primary))" }} />
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold">SDK Configuration</h1>
          <p className="text-muted-foreground">Customize how the SDK looks in your app.</p>
        </div>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No client account found. Please contact support to set up your B2B client.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">SDK Configuration</h1>
          <p className="text-muted-foreground">Customize how the SDK looks in your app.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors">
            <RotateCcw className="h-4 w-4" /> Reset
          </button>
          <button onClick={handleSave} disabled={!hasChanges || isSaving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 transition-colors">
            <Save className="h-4 w-4" /> {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="flex gap-6" style={{ height: "calc(100vh - 140px)" }}>
        {/* Left — Settings (scrollable) */}
        <div className="flex-1 overflow-y-auto pr-2 flex flex-col gap-6 pb-8">

          {/* ═══ Theme Presets ═══ */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Palette className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-base font-semibold">Brand Colors</h3>
              </div>
              <Separator className="mb-4" />

              <p className="text-sm text-muted-foreground mb-3">Choose a preset theme or customize colors</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
                {PRESET_THEMES.map((preset) => {
                  const isActive = activePreset?.name === preset.name;
                  return (
                    <button
                      key={preset.name}
                      onClick={() => applyPreset(preset)}
                      className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                        isActive ? "border-primary shadow-sm" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full" style={{ backgroundColor: preset.dot }} />
                      <span className="text-[11px] font-semibold">{preset.name}</span>
                      {isActive && (
                        <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                          <Check className="h-2.5 w-2.5 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Custom colors toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Custom Colors</p>
                  <p className="text-xs text-muted-foreground">Fine-tune individual colors</p>
                </div>
                <Switch checked={showCustomColors} onCheckedChange={setShowCustomColors} />
              </div>

              {showCustomColors && (
                <div className="mt-3 pt-3 border-t border-border space-y-1">
                  <ColorInput label="Primary" value={settings.primary_color} onChange={(v) => update("primary_color", v)} />
                  <ColorInput label="Accent" value={settings.accent_color} onChange={(v) => update("accent_color", v)} />
                  <ColorInput label="Background" value={settings.background_color} onChange={(v) => update("background_color", v)} />
                  <ColorInput label="Surface" value={settings.surface_color} onChange={(v) => update("surface_color", v)} />
                  <ColorInput label="Text" value={settings.text_color} onChange={(v) => update("text_color", v)} />
                  <ColorInput label="Text Secondary" value={settings.text_secondary_color} onChange={(v) => update("text_secondary_color", v)} />
                  <ColorInput label="Text Muted" value={settings.text_muted_color} onChange={(v) => update("text_muted_color", v)} />
                  <ColorInput label="On Accent" value={settings.on_accent_color} onChange={(v) => update("on_accent_color", v)} />
                  <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    <strong>Text Muted</strong>: used for section headers and dates. Leave empty to inherit from Text Secondary.<br />
                    <strong>On Accent</strong>: text color shown on accent-colored buttons/badges. Leave empty for automatic contrast (black or white).
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ═══ Theme Mode ═══ */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Monitor className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-base font-semibold">Theme Mode</h3>
              </div>
              <Separator className="mb-4" />
              <div className="flex gap-3">
                {[
                  { value: "light", label: "Light", icon: <Sun className="h-5 w-5" />, desc: "Light background with dark text" },
                  { value: "dark", label: "Dark", icon: <Moon className="h-5 w-5" />, desc: "Dark background with light text" },
                ].map((opt) => {
                  const selected = settings.mode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => update("mode", opt.value)}
                      className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div className={`${selected ? "text-primary" : "text-muted-foreground"}`}>{opt.icon}</div>
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${selected ? "text-primary" : ""}`}>{opt.label}</p>
                        <p className="text-xs text-muted-foreground">{opt.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* ═══ Border Radius ═══ */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Layout className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-base font-semibold">Border Radius</h3>
              </div>
              <Separator className="mb-4" />
              <div className="flex gap-3">
                {[
                  { value: "sharp", label: "Sharp", preview: "rounded-[6px]" },
                  { value: "rounded", label: "Rounded", preview: "rounded-[14px]" },
                  { value: "pill", label: "Pill", preview: "rounded-full" },
                ].map((opt) => {
                  const selected = settings.border_radius === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => update("border_radius", opt.value)}
                      className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div
                        className={`w-12 h-8 border-2 ${selected ? "border-primary" : "border-muted-foreground/30"} ${opt.preview}`}
                      />
                      <span className={`text-xs font-semibold ${selected ? "text-primary" : "text-muted-foreground"}`}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* ═══ Branding ═══ */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Type className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-base font-semibold">Branding</h3>
              </div>
              <Separator className="mb-4" />
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Store Title</label>
                  <input type="text" value={settings.store_title} onChange={(e) => update("store_title", e.target.value)} placeholder="Rewards Store" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
                  <p className="text-xs text-muted-foreground mt-1">Displayed in the SDK header</p>
                </div>
                <LogoUploader
                  value={settings.logo_url}
                  onChange={(url) => update("logo_url", url)}
                />
                <div>
                  <label className="text-sm font-medium mb-1.5 block">CTA Button Text</label>
                  <input type="text" value={settings.cta_text} onChange={(e) => update("cta_text", e.target.value)} placeholder="Explore brands" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
                  <p className="text-xs text-muted-foreground mt-1">Main call-to-action button label</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Font Family</label>
                  <input type="text" value={settings.font_family} onChange={(e) => update("font_family", e.target.value)} placeholder="Inter, system-ui, sans-serif" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
                  <p className="text-xs text-muted-foreground mt-1">Custom font for the SDK (must be loaded by the parent app)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* ═══ Section Visibility ═══ */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-base font-semibold">Section Visibility</h3>
              </div>
              <Separator className="mb-4" />
              <p className="text-xs text-muted-foreground mb-4">Toggle which sections are visible in the SDK</p>
              <div className="space-y-3">
                {[
                  { key: "giftcards", label: "Gift Cards", desc: "Popular brands and gift card deals" },
                  { key: "pay", label: "Quick Payments", desc: "Mobile recharge, DTH, electricity, FastTag" },
                  { key: "subscriptions", label: "Shared Subscriptions", desc: "Group subscription marketplace" },
                  { key: "orders", label: "Order History", desc: "Transaction and order tracking" },
                  { key: "chat", label: "Chat", desc: "In-app messaging with group members" },
                  { key: "chat_filters", label: "Chat Filters", desc: "Show/hide All, Groups & Private filter tabs in chats" },
                  { key: "support", label: "Talk to us", desc: "Support chat option in the profile menu" },
                  { key: "search", label: "Search", desc: "Search bar in the header" },
                  { key: "location", label: "Delivery Location", desc: "Address picker pill in the header" },
                  { key: "wallet", label: "Wallet", desc: "User-facing wallet balance + recent activity (refunds, top-ups)" },
                ].map((section) => {
                  const isHidden = settings.hide_sections.includes(section.key);
                  return (
                    <div key={section.key} className="flex items-center justify-between py-2 px-3 rounded-lg border border-border">
                      <div>
                        <p className="text-sm font-medium">{section.label}</p>
                        <p className="text-xs text-muted-foreground">{section.desc}</p>
                      </div>
                      <button
                        onClick={() => {
                          const next = isHidden
                            ? settings.hide_sections.filter((s) => s !== section.key)
                            : [...settings.hide_sections, section.key];
                          update("hide_sections", next);
                        }}
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                        style={{ backgroundColor: isHidden ? "#D1D5DB" : "#059669" }}
                      >
                        <span
                          className="inline-block h-4 w-4 rounded-full bg-white transition-transform"
                          style={{ transform: isHidden ? "translateX(4px)" : "translateX(24px)" }}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* ═══ Hidden Brands ═══ */}
          <HiddenBrandsCard
            brands={brandCatalog}
            loading={brandCatalogLoading}
            hiddenIds={settings.hidden_service_ids}
            onChange={(next) => update("hidden_service_ids", next)}
          />

          {/* ═══ Notifications ═══ */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-base font-semibold">Notifications</h3>
              </div>
              <Separator className="mb-4" />
              <p className="text-xs text-muted-foreground mb-4">Choose how you want to receive transaction reports</p>
              <div className="space-y-3">
                {[
                  { value: "daily", label: "Daily summary", desc: "One email at 9 AM IST with the previous day's activity" },
                  { value: "per_transaction", label: "Per transaction", desc: "Email on every customer purchase (user ID, item, amount)" },
                  { value: "off", label: "Off", desc: "Don't send transaction emails" },
                ].map((opt) => {
                  const selected = settings.email_frequency === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => update("email_frequency", opt.value)}
                      className={`w-full flex items-center justify-between py-3 px-4 rounded-lg border-2 transition-all text-left ${
                        selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                      }`}
                    >
                      <div>
                        <p className={`text-sm font-semibold ${selected ? "text-primary" : ""}`}>{opt.label}</p>
                        <p className="text-xs text-muted-foreground">{opt.desc}</p>
                      </div>
                      {selected && (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0 ml-3">
                          <Check className="h-3 w-3 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right — Live Preview (resizes per device so desktop/tablet stay
            legible). Widened smoothly for a less jarring switch. */}
        <div
          className="hidden lg:flex flex-col items-center flex-shrink-0 transition-[width] duration-200 ease-out"
          style={{ width: PARENT_COLUMN_WIDTH[previewDevice] }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Live Preview</h3>
          </div>
          <SDKPreview
            settings={settings}
            clientId={clientId!}
            authToken={token ?? undefined}
            device={previewDevice}
            onDeviceChange={setPreviewDevice}
          />

          {/* SDK URL */}
          <div className="w-full mt-4 px-2">
            <p className="text-xs font-semibold text-muted-foreground mb-1">SDK URL</p>
            <code className="block text-[10px] font-mono bg-muted p-2 rounded-md break-all select-all cursor-pointer" title="Click to select">
              {`https://sdk.pocoai.store/store?auth=${token}&client=${clientId}`}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
}
